import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

export const usePushNotifications = (enabled = true) => {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [swRegistration, setSwRegistration] = useState(null);
  
  // Check browser support - use useMemo to avoid effect issues
  const isSupported = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return 'Notification' in window && 
           'serviceWorker' in navigator && 
           'PushManager' in window;
  }, []);

  // Initialize permission on mount
  useEffect(() => {
    if (isSupported) {
      setPermission(Notification.permission);
    }
  }, [isSupported]);

  // Register service worker
  useEffect(() => {
    if (!isSupported || !enabled) return;

    const registerSW = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        console.log('Service Worker registered:', registration);
        setSwRegistration(registration);
        
        // Check for existing subscription
        const existingSub = await registration.pushManager.getSubscription();
        if (existingSub) {
          setSubscription(existingSub);
        }
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    };

    registerSW();
  }, [isSupported, enabled]);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.warn('Push notifications not supported');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting permission:', error);
      return false;
    }
  }, [isSupported]);

  // Send notification using Notification API directly
  const sendNotification = useCallback((title, options = {}) => {
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    // Use service worker if available
    if (swRegistration) {
      return swRegistration.showNotification(title, {
        body: options.body || '',
        icon: options.icon || '/favicon.ico',
        badge: '/favicon.ico',
        tag: options.tag || 'notification-' + Date.now(),
        data: options.data || {},
        vibrate: [200, 100, 200],
        requireInteraction: options.requireInteraction || false,
        ...options
      });
    }

    // Fallback to basic Notification API
    return new Notification(title, {
      body: options.body || '',
      icon: options.icon || '/favicon.ico',
      tag: options.tag || 'notification-' + Date.now(),
      ...options
    });
  }, [permission, swRegistration]);

  // Send notification via service worker message
  const showNotificationViaSW = useCallback((payload) => {
    if (!swRegistration?.active) {
      console.warn('No active service worker');
      return;
    }
    
    swRegistration.active.postMessage({
      type: 'SHOW_NOTIFICATION',
      payload
    });
  }, [swRegistration]);

  // Save notification preferences to backend
  const savePreferences = useCallback(async (enabled) => {
    try {
      await axios.put('/users/push-preferences', {
        push_enabled: enabled,
        permission_status: permission
      });
      return true;
    } catch (error) {
      console.error('Failed to save push preferences:', error);
      return false;
    }
  }, [permission]);

  return {
    isSupported,
    permission,
    subscription,
    swRegistration,
    requestPermission,
    sendNotification,
    showNotificationViaSW,
    savePreferences,
    isEnabled: permission === 'granted'
  };
};

export default usePushNotifications;
